import React, { useEffect, useState, useContext, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { CompanyContext } from '../contexts/CompanyContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Plus, ArrowLeft, FileDown, Send, CheckCircle2, Building2, Trash2, Edit2, Wrench, AlertTriangle, Paperclip, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import FileUpload from '../components/FileUpload';
import { generateInspectionPDF } from '../lib/inspectionPdf';

const CATEGORIES = ['paint', 'electrical', 'plumbing', 'hvac', 'joinery', 'civil', 'finishing', 'mep', 'tiling', 'doors_windows', 'ceiling', 'flooring', 'other'];
const SEVERITIES = ['minor', 'major', 'critical'];
const STATUSES = ['open', 'in_progress', 'rectified', 're_inspected', 'closed', 'waived'];

const SEVERITY_COLORS = {
  minor: 'bg-sky-100 text-sky-800 border-sky-200 dark:bg-sky-900/30 dark:text-sky-300',
  major: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300',
  critical: 'bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300',
};

const STATUS_COLORS = {
  open: 'bg-slate-100 text-slate-700',
  in_progress: 'bg-amber-100 text-amber-700',
  rectified: 'bg-violet-100 text-violet-700',
  re_inspected: 'bg-sky-100 text-sky-700',
  closed: 'bg-emerald-100 text-emerald-700',
  waived: 'bg-zinc-200 text-zinc-700',
};

export default function InspectionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { company } = useContext(CompanyContext);
  const [inspection, setInspection] = useState(null);
  const [snags, setSnags] = useState([]);
  const [loading, setLoading] = useState(true);

  // Snag dialog
  const [snagOpen, setSnagOpen] = useState(false);
  const [snagForm, setSnagForm] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Filters
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');

  const [pdfBusy, setPdfBusy] = useState(false);

  // Feb-18 — Checklist rows (tenant check-in/out & customisable)
  const [checklistItems, setChecklistItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [applyingTemplateId, setApplyingTemplateId] = useState('');
  const [addingRow, setAddingRow] = useState(false);

  const canWrite = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR', 'TECHNICIAN'].includes(user?.role);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/inspections/${id}`, { withCredentials: true });
      setInspection(r.data);
      setSnags(r.data.snags || []);
      // Fetch checklist rows + templates in parallel
      const [ci, tpl] = await Promise.all([
        axios.get(`${API}/inspections/${id}/checklist-items`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/checklist-templates`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setChecklistItems(ci.data || []);
      setTemplates(tpl.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load inspection');
      navigate('/inspections');
    }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNewSnag = () => {
    setEditingId(null);
    setSnagForm({
      location: '',
      category: 'other',
      severity: 'minor',
      description: '',
      trade: '',
      target_date: '',
      status: 'open',
      photos_before: [],
      photos_after: [],
      attachments: [],
      notes: '',
    });
    setSnagOpen(true);
  };

  const openEditSnag = (s) => {
    setEditingId(s.id);
    setSnagForm({ ...s });
    setSnagOpen(true);
  };

  const saveSnag = async () => {
    if (!snagForm.description) { toast.error('Description is required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/snags/${editingId}`, snagForm, { withCredentials: true });
        toast.success('Snag updated');
      } else {
        await axios.post(`${API}/inspections/${id}/snags`, snagForm, { withCredentials: true });
        toast.success('Snag added');
      }
      setSnagOpen(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const deleteSnag = async (sid) => {
    if (!window.confirm('Delete this snag?')) return;
    try {
      await axios.delete(`${API}/snags/${sid}`, { withCredentials: true });
      toast.success('Deleted');
      fetchData();
    } catch (e) { toast.error('Delete failed'); }
  };

  const convertToWO = async (sid) => {
    if (!window.confirm('Generate a Work Order from this snag?')) return;
    try {
      const r = await axios.post(`${API}/snags/${sid}/convert-to-wo`, {}, { withCredentials: true });
      toast.success(`Work Order ${r.data.wo_number} created`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Conversion failed'); }
  };

  const submit = async () => {
    try {
      await axios.post(`${API}/inspections/${id}/submit`, {}, { withCredentials: true });
      toast.success('Inspection submitted');
      fetchData();
    } catch (e) { toast.error('Submit failed'); }
  };

  const closeInsp = async () => {
    if (!window.confirm('Close this inspection? It can be reopened only by editing the status.')) return;
    try {
      await axios.post(`${API}/inspections/${id}/close`, {}, { withCredentials: true });
      toast.success('Inspection closed');
      fetchData();
    } catch (e) { toast.error('Close failed'); }
  };

  const downloadPdf = async () => {
    if (!inspection) return;
    setPdfBusy(true);
    try {
      const token = localStorage.getItem('session_token') || '';
      const preview = new URLSearchParams(window.location.search).get('preview_pdf') === '1';
      await generateInspectionPDF({
        inspection,
        snags,
        company,
        attachmentsAuth: token,
        checklistItems,
        view: preview,
      });
    } catch (e) {
      console.error(e);
      toast.error('PDF generation failed');
    }
    setPdfBusy(false);
  };

  // ============ Feb-18 Checklist handlers ============
  const applyTemplate = async () => {
    if (!applyingTemplateId) { toast.error('Pick a template first'); return; }
    try {
      const r = await axios.post(`${API}/inspections/${id}/apply-template/${applyingTemplateId}`, {}, { withCredentials: true });
      toast.success(`Added ${r.data.inserted || 0} rows from template`);
      setApplyingTemplateId('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to apply template');
    }
  };

  const addChecklistRow = async () => {
    setAddingRow(true);
    try {
      await axios.post(`${API}/inspections/${id}/checklist-items`, {
        details: '', findings: '', action_required: '', charged_to: 'na', cost: null, remarks: '',
      }, { withCredentials: true });
      const r = await axios.get(`${API}/inspections/${id}/checklist-items`, { withCredentials: true });
      setChecklistItems(r.data || []);
    } catch (e) {
      toast.error('Failed to add row');
    }
    setAddingRow(false);
  };

  const updateChecklistRow = async (itemId, patch) => {
    // optimistic
    setChecklistItems((prev) => prev.map(x => x.id === itemId ? { ...x, ...patch } : x));
    try {
      await axios.put(`${API}/checklist-items/${itemId}`, patch, { withCredentials: true });
    } catch (e) {
      toast.error('Save failed');
      fetchData();
    }
  };

  const deleteChecklistRow = async (itemId) => {
    if (!window.confirm('Delete this checklist row?')) return;
    try {
      await axios.delete(`${API}/checklist-items/${itemId}`, { withCredentials: true });
      setChecklistItems((prev) => prev.filter(x => x.id !== itemId));
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  // ============ Feb-18 Cost approval handlers ============
  const canApproveCost = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER'].includes(user?.role);
  const [revisingCost, setRevisingCost] = useState(false);
  const [reviseOpen, setReviseOpen] = useState(false);
  const [reviseAmount, setReviseAmount] = useState('');
  const [reviseNote, setReviseNote] = useState('');

  const submitCostApproval = async () => {
    if (!window.confirm('Submit this inspection\'s total cost for management approval?')) return;
    try {
      await axios.post(`${API}/inspections/${id}/submit-cost-approval`, {}, { withCredentials: true });
      toast.success('Sent for cost approval');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit'); }
  };

  const approveCost = async () => {
    if (!window.confirm('Approve current total as FINAL AGREED COST?')) return;
    try {
      const r = await axios.post(`${API}/inspections/${id}/approve-cost`, {}, { withCredentials: true });
      toast.success(`Approved: ${inspection.currency || 'AED'} ${Number(r.data.final_agreed_cost).toLocaleString()}`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
  };

  const rejectCost = async () => {
    const reason = window.prompt('Reason for rejection?', '');
    if (reason === null) return;
    try {
      await axios.post(`${API}/inspections/${id}/reject-cost`, { reason }, { withCredentials: true });
      toast.success('Cost rejected — inspection returned for revision');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reject failed'); }
  };

  const openRevise = () => {
    setReviseAmount(String(inspection.final_agreed_cost ?? (inspection.total_cost || 0) + checklistTotal));
    setReviseNote('');
    setReviseOpen(true);
  };

  const submitRevise = async () => {
    const nc = parseFloat(reviseAmount);
    if (isNaN(nc) || nc < 0) { toast.error('Enter a valid amount'); return; }
    setRevisingCost(true);
    try {
      await axios.put(`${API}/inspections/${id}/revise-cost`, {
        new_cost: nc, note: reviseNote,
      }, { withCredentials: true });
      toast.success('Cost revised — recorded in audit log');
      setReviseOpen(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Revise failed'); }
    setRevisingCost(false);
  };

  const checklistTotal = checklistItems.reduce((sum, x) => sum + (Number(x.cost) || 0), 0);
  const tenantChargeTotal = checklistItems.filter(x => x.charged_to === 'tenant').reduce((sum, x) => sum + (Number(x.cost) || 0), 0);
  const companyChargeTotal = checklistItems.filter(x => x.charged_to === 'company').reduce((sum, x) => sum + (Number(x.cost) || 0), 0);

  const visibleSnags = snags.filter(s => {
    if (filterSeverity !== 'all' && s.severity !== filterSeverity) return false;
    if (filterStatus !== 'all' && s.status !== filterStatus) return false;
    if (filterCategory !== 'all' && s.category !== filterCategory) return false;
    return true;
  });

  // Group by location for room-by-room readability
  const grouped = visibleSnags.reduce((acc, s) => {
    const key = s.location || '(No location)';
    if (!acc[key]) acc[key] = [];
    acc[key].push(s);
    return acc;
  }, {});

  if (loading || !inspection) {
    return (
      <Layout><div className="py-20 text-center text-muted-foreground">Loading inspection…</div></Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="inspection-detail-page" className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/inspections')} className="mb-2 -ml-2">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to inspections
            </Button>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground dark:text-slate-100">{inspection.title}</h1>
            <div className="flex flex-wrap gap-3 text-sm text-muted-foreground dark:text-slate-400 mt-1">
              <span className="font-mono">{inspection.inspection_number}</span>
              <span className="capitalize">{(inspection.inspection_type || '').replace(/_/g, ' ')}</span>
              {inspection.building_name && <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" /> {inspection.building_name}{inspection.unit_number ? ` · Unit ${inspection.unit_number}` : ''}</span>}
              <span>Inspector: {inspection.inspector_name}</span>
              <span>{inspection.inspection_date}</span>
              <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLORS[inspection.status] || 'bg-slate-100'}`}>{inspection.status?.replace(/_/g, ' ')}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button data-testid="download-pdf-btn" variant="outline" onClick={downloadPdf} disabled={pdfBusy}>
              <FileDown className="w-4 h-4 mr-1" /> {pdfBusy ? 'Building…' : 'Download Report PDF'}
            </Button>
            {canWrite && inspection.status === 'draft' && (
              <Button variant="outline" onClick={submit}><Send className="w-4 h-4 mr-1" /> Submit</Button>
            )}
            {canWrite && inspection.status !== 'closed' && (
              <Button variant="outline" onClick={closeInsp}><CheckCircle2 className="w-4 h-4 mr-1" /> Close</Button>
            )}
            {canWrite && (
              <Button data-testid="add-snag-btn" onClick={openNewSnag} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                <Plus className="w-4 h-4 mr-1" /> Add Snag
              </Button>
            )}
          </div>
        </div>

        {/* Counters — Feb-17: added Total Repair Cost card */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Card className="p-3"><div className="text-xs text-muted-foreground">Total Snags</div><div className="text-2xl font-bold">{inspection.total_snags ?? 0}</div></Card>
          <Card className="p-3 border-amber-200"><div className="text-xs text-amber-700">Open</div><div className="text-2xl font-bold text-amber-700">{inspection.open_snags ?? 0}</div></Card>
          <Card className="p-3 border-rose-200"><div className="text-xs text-rose-700">Critical</div><div className="text-2xl font-bold text-rose-700">{inspection.critical_snags ?? 0}</div></Card>
          <Card className="p-3 border-emerald-200"><div className="text-xs text-emerald-700">Closed / Waived</div><div className="text-2xl font-bold text-emerald-700">{inspection.closed_snags ?? 0}</div></Card>
          <Card data-testid="total-cost-card" className="p-3 border-teal-200 bg-teal-50/40">
            <div className="text-xs text-teal-700">Total Repair Cost</div>
            <div className="text-2xl font-bold text-teal-800">{inspection.currency || 'AED'} {(inspection.total_cost || 0).toLocaleString()}</div>
          </Card>
        </div>

        {/* Inspection-level attachments */}
        {canWrite && (
          <Card className="p-4">
            <div className="text-sm font-semibold mb-2 flex items-center gap-2"><Paperclip className="w-4 h-4" /> General Attachments (PDFs, plans, images)</div>
            <FileUpload
              value={inspection.attachments || []}
              maxFiles={10}
              onChange={async (urls) => {
                try {
                  await axios.put(`${API}/inspections/${id}`, { attachments: urls }, { withCredentials: true });
                  setInspection({ ...inspection, attachments: urls });
                } catch { toast.error('Failed to save attachments'); }
              }}
            />
          </Card>
        )}

        {/* Feb-18 — Cost Approval workflow (mirrors material approval) */}
        <Card data-testid="cost-approval-card" className="p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <div className="text-sm font-semibold flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Final Cost Approval
                </div>
                {inspection.cost_status === 'draft' && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">Draft</span>}
                {inspection.cost_status === 'pending_approval' && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">Pending Approval</span>}
                {inspection.cost_status === 'approved' && <span data-testid="cost-approved-badge" className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 font-semibold">APPROVED</span>}
                {inspection.cost_status === 'rejected' && <span className="text-xs px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 border border-rose-200">Rejected</span>}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {inspection.cost_status === 'draft' && 'Submit the total cost (snags + checklist) for Management approval.'}
                {inspection.cost_status === 'pending_approval' && `Awaiting management approval · submitted by ${inspection.submitted_for_cost_approval_by_name || '—'}`}
                {inspection.cost_status === 'approved' && `Approved by ${inspection.cost_approved_by_name} on ${inspection.cost_approved_at ? new Date(inspection.cost_approved_at).toLocaleString() : '—'}`}
                {inspection.cost_status === 'rejected' && `Rejected — ${inspection.cost_rejected_reason || 'no reason'}`}
              </div>
              {inspection.cost_status === 'approved' && (
                <div className="mt-2 inline-flex items-center gap-3 px-4 py-2 rounded-lg bg-emerald-50 border border-emerald-200">
                  <div className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">Final Agreed Cost</div>
                  <div className="text-xl font-bold text-emerald-900 font-mono" data-testid="final-agreed-cost">
                    {inspection.currency || 'AED'} {Number(inspection.final_agreed_cost || 0).toLocaleString()}
                  </div>
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {canWrite && inspection.cost_status !== 'approved' && inspection.cost_status !== 'pending_approval' && (
                <Button data-testid="submit-cost-approval-btn" size="sm" variant="outline" onClick={submitCostApproval}>
                  <Send className="w-4 h-4 mr-1" /> Submit for Approval
                </Button>
              )}
              {canApproveCost && inspection.cost_status === 'pending_approval' && (
                <>
                  <Button data-testid="approve-cost-btn" size="sm" onClick={approveCost} className="bg-emerald-500 hover:bg-emerald-600 text-white">
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Approve Final Cost
                  </Button>
                  <Button data-testid="reject-cost-btn" size="sm" variant="outline" onClick={rejectCost} className="text-rose-700 border-rose-300">
                    Reject
                  </Button>
                </>
              )}
              {canApproveCost && (inspection.cost_status === 'approved' || inspection.cost_status === 'rejected') && (
                <Button data-testid="revise-cost-btn" size="sm" variant="outline" onClick={openRevise}>
                  <Edit2 className="w-4 h-4 mr-1" /> Revise Cost (Manager)
                </Button>
              )}
            </div>
          </div>

          {(inspection.cost_revisions || []).length > 0 && (
            <div className="mt-3 border-t pt-3">
              <div className="text-xs font-semibold text-muted-foreground mb-1">Revision History (Audit Trail)</div>
              <ul className="text-xs space-y-1">
                {(inspection.cost_revisions || []).map((r, i) => (
                  <li key={i} data-testid={`revision-row-${i}`} className="text-slate-700">
                    <span className="font-semibold">Revised cost by {r.revised_by_name}</span> on {new Date(r.revised_at).toLocaleString()} —{' '}
                    <span className="font-mono">{inspection.currency || 'AED'} {Number(r.old_cost || 0).toLocaleString()} → {Number(r.new_cost || 0).toLocaleString()}</span>
                    {r.note ? <span className="text-muted-foreground"> · {r.note}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>

        {/* Feb-18 — Standard Checklist (customizable, tenant check-in/out) */}
        <Card className="p-4" data-testid="checklist-section">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div>
              <div className="text-sm font-semibold flex items-center gap-2">
                <ClipboardList className="w-4 h-4" /> Standard Checklist
              </div>
              <div className="text-xs text-muted-foreground">
                Track items handed over, damages, missing accessories, deductions & who pays.
              </div>
            </div>
            {canWrite && (
              <div className="flex flex-wrap gap-2 items-center">
                <Select value={applyingTemplateId} onValueChange={setApplyingTemplateId}>
                  <SelectTrigger data-testid="checklist-template-select" className="h-9 w-[240px]">
                    <SelectValue placeholder="Choose a template…" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.length === 0 ? (
                      <SelectItem value="__none" disabled>No templates yet</SelectItem>
                    ) : (
                      templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)
                    )}
                  </SelectContent>
                </Select>
                <Button data-testid="apply-template-btn" size="sm" variant="outline" onClick={applyTemplate} disabled={!applyingTemplateId}>
                  Apply Template
                </Button>
                <Button data-testid="add-checklist-row-btn" size="sm" onClick={addChecklistRow} disabled={addingRow} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                  <Plus className="w-4 h-4 mr-1" /> Add Row
                </Button>
              </div>
            )}
          </div>

          {checklistItems.length === 0 ? (
            <div className="border border-dashed rounded-lg py-8 text-center text-sm text-muted-foreground">
              No checklist rows. Apply a template or click <b>Add Row</b>.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800/40 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-2 py-2 w-12">S.No</th>
                      <th className="px-2 py-2 text-left min-w-[160px]">Details</th>
                      <th className="px-2 py-2 text-left min-w-[160px]">Findings</th>
                      <th className="px-2 py-2 text-left min-w-[160px]">Action Required</th>
                      <th className="px-2 py-2 text-right w-28">Cost</th>
                      <th className="px-2 py-2 text-left w-36">Charged To</th>
                      <th className="px-2 py-2 text-left min-w-[140px]">Remarks</th>
                      {canWrite && <th className="w-10"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {checklistItems.map((row, idx) => (
                      <tr key={row.id} className="border-t align-top" data-testid={`checklist-row-${idx}`}>
                        <td className="px-2 py-1.5 text-center text-xs text-muted-foreground">{row.sno || (idx + 1)}</td>
                        <td className="px-2 py-1.5">
                          <Input data-testid={`chk-details-${idx}`} value={row.details || ''} disabled={!canWrite} onChange={(e) => updateChecklistRow(row.id, { details: e.target.value })} placeholder="e.g. Front door keys" />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input value={row.findings || ''} disabled={!canWrite} onChange={(e) => updateChecklistRow(row.id, { findings: e.target.value })} placeholder="e.g. 1 key missing" />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input value={row.action_required || ''} disabled={!canWrite} onChange={(e) => updateChecklistRow(row.id, { action_required: e.target.value })} placeholder="e.g. Cut new key" />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input type="number" min="0" step="0.01" className="text-right" disabled={!canWrite}
                            value={row.cost ?? ''}
                            onChange={(e) => updateChecklistRow(row.id, { cost: e.target.value === '' ? null : parseFloat(e.target.value) })}
                            placeholder="0" />
                        </td>
                        <td className="px-2 py-1.5">
                          <Select value={row.charged_to || 'na'} onValueChange={(v) => updateChecklistRow(row.id, { charged_to: v })} disabled={!canWrite}>
                            <SelectTrigger data-testid={`chk-charged-${idx}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="na">N/A</SelectItem>
                              <SelectItem value="tenant">Tenant</SelectItem>
                              <SelectItem value="company">Company</SelectItem>
                              <SelectItem value="shared">Shared</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-1.5">
                          <Input value={row.remarks || ''} disabled={!canWrite} onChange={(e) => updateChecklistRow(row.id, { remarks: e.target.value })} />
                        </td>
                        {canWrite && (
                          <td className="px-2 py-1.5">
                            <Button size="sm" variant="ghost" onClick={() => deleteChecklistRow(row.id)}>
                              <Trash2 className="w-4 h-4 text-rose-600" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 flex flex-wrap gap-4 justify-end text-xs">
                <span className="px-3 py-1.5 rounded bg-slate-100 text-slate-700">Total: <b className="font-mono">{inspection.currency || 'AED'} {checklistTotal.toLocaleString()}</b></span>
                <span className="px-3 py-1.5 rounded bg-amber-50 text-amber-800 border border-amber-200">Tenant: <b className="font-mono">{inspection.currency || 'AED'} {tenantChargeTotal.toLocaleString()}</b></span>
                <span className="px-3 py-1.5 rounded bg-sky-50 text-sky-800 border border-sky-200">Company: <b className="font-mono">{inspection.currency || 'AED'} {companyChargeTotal.toLocaleString()}</b></span>
              </div>
            </>
          )}
        </Card>

        {/* Snag filters */}
        <Card className="p-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col">
              <Label className="text-xs text-muted-foreground mb-1">Severity</Label>
              <Select value={filterSeverity} onValueChange={setFilterSeverity}>
                <SelectTrigger className="h-9 w-[140px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All severities</SelectItem>
                  {SEVERITIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col">
              <Label className="text-xs text-muted-foreground mb-1">Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="h-9 w-[140px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col">
              <Label className="text-xs text-muted-foreground mb-1">Category</Label>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="h-9 w-[160px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace(/_/g, ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="text-xs text-muted-foreground ml-auto">Showing {visibleSnags.length} of {snags.length}</div>
          </div>
        </Card>

        {/* Snag grid grouped by location */}
        {Object.keys(grouped).length === 0 ? (
          <Card className="p-12 text-center text-muted-foreground" data-testid="no-snags">
            No snags yet.{canWrite && ' Click Add Snag to log a defect.'}
          </Card>
        ) : (
          Object.entries(grouped).map(([loc, list]) => (
            <Card key={loc} className="p-4">
              <h3 className="font-semibold mb-3 text-slate-800 dark:text-slate-200">{loc} <span className="text-xs text-muted-foreground font-normal">({list.length})</span></h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {list.map((s, idx) => (
                  <div key={s.id} data-testid={`snag-card-${s.snag_number}`} className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-800/40 flex flex-col gap-2">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-mono text-xs text-muted-foreground">{s.snag_number}</div>
                        <div className="font-medium text-sm">{s.description}</div>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded border whitespace-nowrap ${SEVERITY_COLORS[s.severity] || ''}`}>
                        {s.severity?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1 text-[11px]">
                      <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 capitalize">{s.category?.replace(/_/g, ' ')}</span>
                      {s.trade && <span className="px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">{s.trade}</span>}
                      <span className={`px-1.5 py-0.5 rounded ${STATUS_COLORS[s.status] || ''}`}>{s.status?.replace(/_/g, ' ')}</span>
                      {s.repair_cost != null && s.repair_cost > 0 && (
                        <span data-testid={`snag-cost-${s.snag_number}`} className="px-1.5 py-0.5 rounded bg-teal-100 text-teal-800 font-mono">
                          {inspection.currency || 'AED'} {Number(s.repair_cost).toLocaleString()}
                        </span>
                      )}
                      {s.work_order_id && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 inline-flex items-center gap-1"><Wrench className="w-3 h-3" /> WO linked</span>}
                    </div>
                    {(s.photos_before?.length > 0 || s.photos_after?.length > 0) && (
                      <div className="flex gap-1 overflow-x-auto py-1">
                        {(s.photos_before || []).slice(0, 3).map((u, i) => (
                          <a key={'b' + i} href={u} target="_blank" rel="noopener noreferrer" title="Before">
                            <img src={u} alt="before" className="w-14 h-14 object-cover rounded border border-rose-300" />
                          </a>
                        ))}
                        {(s.photos_after || []).slice(0, 3).map((u, i) => (
                          <a key={'a' + i} href={u} target="_blank" rel="noopener noreferrer" title="After">
                            <img src={u} alt="after" className="w-14 h-14 object-cover rounded border border-emerald-300" />
                          </a>
                        ))}
                      </div>
                    )}
                    {canWrite && (
                      <div className="flex justify-end gap-1 mt-1">
                        {!s.work_order_id && (
                          <Button data-testid={`snag-convert-${idx}`} size="sm" variant="outline" title="Convert to Work Order" onClick={() => convertToWO(s.id)}>
                            <Wrench className="w-3 h-3" />
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => openEditSnag(s)}><Edit2 className="w-3 h-3" /></Button>
                        <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => deleteSnag(s.id)}><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          ))
        )}

        {/* Feb-18 — Revise Cost dialog (MANAGER+ only) */}
        <Dialog open={reviseOpen} onOpenChange={setReviseOpen}>
          <DialogContent data-testid="revise-cost-dialog" className="max-w-md">
            <DialogHeader>
              <DialogTitle>Revise Final Agreed Cost</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground">
                Manager-only override. The change will be recorded in the audit log and shown on the PDF as
                &ldquo;Revised cost by (your name) on (date)&rdquo;.
              </div>
              <div>
                <Label>New Cost ({inspection.currency || 'AED'})</Label>
                <Input data-testid="revise-cost-input" type="number" min="0" step="0.01"
                  value={reviseAmount} onChange={(e) => setReviseAmount(e.target.value)} />
              </div>
              <div>
                <Label>Note / Reason (optional)</Label>
                <Textarea data-testid="revise-cost-note" rows={3} value={reviseNote}
                  onChange={(e) => setReviseNote(e.target.value)}
                  placeholder="e.g. Negotiated with tenant after appeal" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReviseOpen(false)}>Cancel</Button>
              <Button data-testid="revise-cost-save-btn" onClick={submitRevise} disabled={revisingCost} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                {revisingCost ? 'Saving…' : 'Save Revision'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Snag dialog */}
        <Dialog open={snagOpen} onOpenChange={setSnagOpen}>
          <DialogContent data-testid="snag-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit Snag' : 'Add Snag'}{snagForm.snag_number ? ` — ${snagForm.snag_number}` : ''}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Location *</Label>
                  <Input data-testid="snag-location" value={snagForm.location || ''} onChange={(e) => setSnagForm({ ...snagForm, location: e.target.value })} placeholder="e.g. Master Bedroom" />
                </div>
                <div><Label>Trade / Contractor</Label>
                  <Input value={snagForm.trade || ''} onChange={(e) => setSnagForm({ ...snagForm, trade: e.target.value })} placeholder="e.g. Painting Co." />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Category</Label>
                  <Select value={snagForm.category || 'other'} onValueChange={(v) => setSnagForm({ ...snagForm, category: v })}>
                    <SelectTrigger data-testid="snag-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace(/_/g, ' ')}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Severity</Label>
                  <Select value={snagForm.severity || 'minor'} onValueChange={(v) => setSnagForm({ ...snagForm, severity: v })}>
                    <SelectTrigger data-testid="snag-severity"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SEVERITIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Status</Label>
                  <Select value={snagForm.status || 'open'} onValueChange={(v) => setSnagForm({ ...snagForm, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Description *</Label>
                <Textarea data-testid="snag-description" rows={3} value={snagForm.description || ''} onChange={(e) => setSnagForm({ ...snagForm, description: e.target.value })} placeholder="What's the defect? Where exactly? Suggested fix?" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Target Completion Date</Label>
                  <Input type="date" value={snagForm.target_date || ''} onChange={(e) => setSnagForm({ ...snagForm, target_date: e.target.value })} />
                </div>
                <div><Label>Assigned To (name)</Label>
                  <Input value={snagForm.assigned_to_name || ''} onChange={(e) => setSnagForm({ ...snagForm, assigned_to_name: e.target.value })} />
                </div>
              </div>

              {/* Feb-17 — repair cost input */}
              <div className="grid grid-cols-2 gap-3 p-3 border border-teal-200 rounded-lg bg-teal-50/40">
                <div>
                  <Label className="text-teal-800">Repair Cost ({inspection.currency || 'AED'})</Label>
                  <Input
                    data-testid="snag-cost-input"
                    type="number"
                    min="0"
                    step="0.01"
                    value={snagForm.repair_cost ?? ''}
                    onChange={(e) => setSnagForm({ ...snagForm, repair_cost: e.target.value === '' ? null : parseFloat(e.target.value) })}
                    placeholder="e.g., 450"
                  />
                </div>
                <div>
                  <Label className="text-teal-800">Cost Notes</Label>
                  <Input
                    value={snagForm.cost_notes || ''}
                    onChange={(e) => setSnagForm({ ...snagForm, cost_notes: e.target.value })}
                    placeholder="Estimate / quote source"
                  />
                </div>
              </div>


              <div className="rounded-lg border border-rose-200 dark:border-rose-800/40 bg-rose-50/40 dark:bg-rose-900/10 p-3 space-y-2">
                <Label className="text-rose-800 dark:text-rose-300 text-sm font-semibold flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Photos — BEFORE (defect evidence)</Label>
                <FileUpload value={snagForm.photos_before || []} maxFiles={8} onChange={(urls) => setSnagForm({ ...snagForm, photos_before: urls })} accept="image/*" />
              </div>

              <div className="rounded-lg border border-emerald-200 dark:border-emerald-800/40 bg-emerald-50/40 dark:bg-emerald-900/10 p-3 space-y-2">
                <Label className="text-emerald-800 dark:text-emerald-300 text-sm font-semibold flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Photos — AFTER (post-rectification)</Label>
                <FileUpload value={snagForm.photos_after || []} maxFiles={8} onChange={(urls) => setSnagForm({ ...snagForm, photos_after: urls })} accept="image/*" />
              </div>

              <div>
                <Label className="text-sm font-semibold flex items-center gap-1"><Paperclip className="w-4 h-4" /> Other Attachments (PDFs, docs)</Label>
                <FileUpload value={snagForm.attachments || []} maxFiles={5} onChange={(urls) => setSnagForm({ ...snagForm, attachments: urls })} />
              </div>

              <div><Label>Notes</Label>
                <Textarea rows={2} value={snagForm.notes || ''} onChange={(e) => setSnagForm({ ...snagForm, notes: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSnagOpen(false)}>Cancel</Button>
              <Button data-testid="snag-save-btn" onClick={saveSnag} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                {saving ? 'Saving…' : (editingId ? 'Update Snag' : 'Add Snag')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
